<template>
  <v-chart
    ref="tempRiseChart"
    id="tempRiseChart"
    class="chart"
    :init-options="initOptions"
    style="width: 600px; height: 600px;"
    @change="generateValues"
    :manual-update="true"
  />
</template>
<script lang="ts" setup>
import {lookupTable, ultrasoundBeamDiameterArray, ultrasoundFrequencykHZArray} from "../stores/lookuptable";
import * as echarts from 'echarts/core'
import { SVGRenderer } from 'echarts/renderers'
import { useCalculatorStore } from '../stores/calculator.store'
import {
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  GraphicComponent
} from 'echarts/components'
import { LineChart } from 'echarts/charts'
import { UniversalTransition } from 'echarts/features'
import VChart from 'vue-echarts'
import {onMounted, ref} from 'vue';
import type ECharts from "vue-echarts";
import {storeToRefs} from "pinia";
const calculatorStore = useCalculatorStore()

echarts.use([
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  SVGRenderer,
  UniversalTransition,
  GraphicComponent
])
const {
  isDarkModeGlobal,
} = storeToRefs(calculatorStore)
const tempRiseChart = ref<InstanceType<typeof ECharts> | null>(null)

const initOptions = {
  renderer: "svg",
  devicePixelRatio: 2,
}

const data = Object.entries(lookupTable).map((k) => {
  const key = k[0].split(',');
  return [ultrasoundFrequencykHZArray.indexOf(+key[0]), ultrasoundBeamDiameterArray.indexOf(+key[1]), k[1] || '-']
})


const commonTextStyle = {
  color: isDarkModeGlobal.value ? "#FFF" : "#333",
  fontFamily: "'Roboto', '-apple-system', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  fontWeight: 'normal'
};

const titleTextStyle = {
  ...commonTextStyle,
  fontSize: 20,
};
const generateValues = () => {
  if (tempRiseChart.value) {
    tempRiseChart.value.setOption({
    title: {
        text: 'PC95 of all skull points',
        left: 'center',
        textStyle: titleTextStyle
    },
    grid: {
      height: '400',
      top: '10%',
      right: '15%'
    },
    minorTick: {
      show: false
    },
    minorSplitLine: {
      show: true
    },
    xAxis: {
      name: 'Ultrasound frequency (kHz)',
      nameLocation: 'middle',
      nameTextStyle: {
        color: commonTextStyle.color,
        padding: 12,
      },
      textStyle: {
       fontSize: 22,
      },
      type: 'category',
      data: ultrasoundFrequencykHZArray,
      splitArea: {
        show: true
      }
    },
    tooltip: {
      trigger: 'item',       // Trigger on each cell
      axisPointer: {
      type: 'none'         // Disable axis hover effects
    },
    formatter: (params) => {
        const { value } = params;
        return `Frequency: ${ultrasoundFrequencykHZArray[value[0]]} kHz<br/>
              Beam Diameter: ${ultrasoundBeamDiameterArray[value[1]]} mm<br/>
                            Maximum Pressure Transmission: ${value[2]} %`;
      }
    },
    yAxis: {
      name: 'Ultrasound beam diameter on the skull surface (mm)',
      nameLocation: 'center',
      nameTextStyle: {
        color: commonTextStyle.color,
        padding: 16
      },
      type: 'category',
      data: ultrasoundBeamDiameterArray,
      splitArea: {
        show: true
      }
    },
    visualMap: {
      min: 30,
      max: 100,
      calculable: true,
      orient: 'vertical',
      right: '20',
      top: '140',
      realtime: false,
      inRange: {
        color: [
          '#000791',
          '#0060cf',
          '#007bd1',
          '#33e890',
          '#24ff00',
          '#73ed4a',
          '#ffbb00',
          '#ff8200',
          '#ed3900',
          '#ff0900',
          '#ff0036'
        ]
      }
    },
    graphic: [{
      type: 'text',
      right: '0',
      top: '100',
      style: {
        text: 'Maximum Pressure Transmission (%)',
        fill: isDarkModeGlobal.value ? "#FFF" : "#333",
        font: 'bold 14px Arial',
        align: 'center',
        textAlign: 'center',
        textVerticalAlign: 'middle'
      },
      rotation: Math.PI / 2   // Rotate 90 degrees counterclockwise
    }],
    series: [{
      name: 'Maximum Pressure Transmission (%)',
        type: 'heatmap',
        data: data,
        label: {
          show: true
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  })
  }
}

onMounted(() => generateValues())
</script>
<style scoped>
  div {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
</style>
